import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, MapPin, Users, ChevronRight } from "lucide-react";
import { formatInTimeZone } from "date-fns-tz";

interface ProducerEvent {
  id: string;
  title: string;
  date: string;
  location: string;
  image_url: string | null;
  status: string;
  timezone: string;
  currency: string | null;
}

interface ProducerEventsListProps {
  events: ProducerEvent[];
  onSelectEvent: (eventId: string) => void;
  selectedEventId: string | null;
}

export const ProducerEventsList = ({ 
  events, 
  onSelectEvent, 
  selectedEventId 
}: ProducerEventsListProps) => {
  const formatEventDate = (date: string, timezone: string = 'UTC') => {
    try {
      return formatInTimeZone(new Date(date), timezone, 'EEE, d MMM yyyy');
    } catch {
      return formatInTimeZone(new Date(date), 'UTC', 'EEE, d MMM yyyy');
    }
  };

  if (events.length === 0) {
    return (
      <div className="text-center py-12 space-y-4">
        <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
          <CalendarDays className="w-8 h-8 text-primary" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">No events yet</h3>
          <p className="text-muted-foreground text-sm">Create your first event to get started</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {events.map((event) => (
        <Card
          key={event.id}
          className={`cursor-pointer transition-all duration-200 hover:bg-accent/50 ${
            selectedEventId === event.id 
              ? 'ring-2 ring-primary bg-accent/30' 
              : 'bg-card'
          }`}
          onClick={() => onSelectEvent(event.id)}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              {/* Event Image */}
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                {event.image_url ? (
                  <img
                    src={event.image_url}
                    alt={event.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-primary/30 to-purple-500/30 flex items-center justify-center">
                    <CalendarDays className="w-6 h-6 text-primary" />
                  </div>
                )}
              </div>

              {/* Event Info */}
              <div className="flex-1 min-w-0 space-y-1">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-foreground truncate">
                    {event.title}
                  </h3>
                  <Badge 
                    variant={event.status === 'published' ? 'default' : 'secondary'}
                    className="flex-shrink-0 text-xs"
                  >
                    {event.status}
                  </Badge>
                </div>
                
                <div className="flex items-center gap-1 text-muted-foreground text-sm">
                  <CalendarDays className="w-3.5 h-3.5" />
                  <span>{formatEventDate(event.date, event.timezone)}</span>
                </div>
                
                <div className="flex items-center gap-1 text-muted-foreground text-sm">
                  <MapPin className="w-3.5 h-3.5" />
                  <span className="truncate">{event.location}</span>
                </div>
              </div>

              {/* Arrow indicator */}
              <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
